import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, Search, MapPin, Sparkles, Droplets, Heart, Truck, CalendarHeart, ChevronDown, Stethoscope, Building2, Pill } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import LogoImage from "@/components/ui/logo-image";
import PremiumMegaMenu from "@/components/home/PremiumMegaMenu";
import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import PatientAuthModal from "@/components/patient/PatientAuthModal";
import { useAllModulesConfig } from "@/hooks/useAuthConfig";
import { isAdminPreview } from "@/hooks/useModuleVisibility";
import { lockBodyScroll } from "@/lib/scrollLock";

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPatientAuth, setShowPatientAuth] = useState(false);
  const { logoHeight, logoHeightMobile } = useSiteSettings();

  useEffect(() => {
    if (!isMenuOpen) return;
    return lockBodyScroll();
  }, [isMenuOpen]);

  // Module visibility — admin preview sees everything
  const moduleConfig = useAllModulesConfig();
  const adminPreview = isAdminPreview();
  const isModuleVisible = (key: string) => {
    if (adminPreview) return true;
    const status = moduleConfig?.[key]?.visibility_status ?? 'LIVE';
    return status === 'LIVE';
  };

  const allNavLinks = [
    { name: "Home", href: "/", isRoute: true, moduleKey: null as string | null },
    { name: "Hospitals", href: "/listings?category=1", isRoute: true, moduleKey: 'hospital' },
    { name: "Doctors", href: "/listings?category=2", isRoute: true, moduleKey: 'doctor' },
    { name: "Pharmacies", href: "/listings?category=3", isRoute: true, moduleKey: 'medical_store' },
    { name: "Blood Banks", href: "/blood-banks", isRoute: true, highlight: true, moduleKey: 'blood_bank' },
  ];
  const navLinks = allNavLinks.filter(l => !l.moduleKey || isModuleVisible(l.moduleKey));

  const allSpecialServices = [
    { name: "HomeCare+", href: "/homecare", icon: Heart, color: "text-primary", bgColor: "bg-primary/10", description: "Professional home healthcare services", moduleKey: 'homecare' },
    { name: "Ambulance", href: "/ambulance", icon: Truck, color: "text-primary", bgColor: "bg-primary/10", description: "24/7 emergency ambulance booking", moduleKey: 'ambulance' },
    { name: "Health Camps", href: "/health-camps", icon: CalendarHeart, color: "text-primary", bgColor: "bg-primary/10", description: "Free & discounted health checkups", moduleKey: 'health_camp' },
  ];
  const specialServices = allSpecialServices.filter(s => isModuleVisible(s.moduleKey));

  const allMoreServices = [
    { name: "Diagnostic Centers", href: "/listings?category=6", icon: Stethoscope, color: "text-primary", moduleKey: 'diagnostic' },
    { name: "Wholesalers", href: "/listings?category=7", icon: Building2, color: "text-cta-orange", moduleKey: 'wholesaler' },
    { name: "All Listings", href: "/listings", icon: Pill, color: "text-healing-green", moduleKey: null as string | null },
  ];
  const moreServices = allMoreServices.filter(s => !s.moduleKey || isModuleVisible(s.moduleKey));

  return (
    <>
    <header className={cn("sticky top-0", isMenuOpen ? "z-[80]" : "z-50")}>
      {/* Top Bar — padding-top: env(safe-area-inset-top) pushes the real,
          tappable header content below the iOS status bar/notch/Dynamic
          Island. Without it, an installed PWA (standalone display-mode —
          no browser chrome, so the page starts right at the physical
          screen edge) renders this bar's content partly under that system
          UI; taps landing there get swallowed by iOS instead of reaching
          the page, which is exactly what made the hamburger menu button
          "not respond" after install on iPhone while working fine in a
          regular Safari tab (where the URL bar already occupies that
          space). The gradient background fills the safe-area gap too, so
          it reads as an intentional colored status bar, not a blank strip. */}
      <div style={{ background: 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))', paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-2.5 flex justify-between items-center text-white text-xs sm:text-sm gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span className="font-medium truncate">
              <span className="sm:hidden">Verified Healthcare Near You</span>
              <span className="hidden sm:inline">Find Verified Healthcare Near You</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <Sparkles className="w-4 h-4" />
            <span>India's #1 Healthcare Directory</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-card/95 backdrop-blur-xl border-b border-border/50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <LogoImage
                alt="PataKare - Experience Medically"
                style={{ height: `${logoHeightMobile || 48}px` }}
                className="sm:hidden w-auto object-contain"
              />
              <LogoImage
                alt="PataKare - Experience Medically"
                style={{ height: `${logoHeight || 56}px` }}
                className="hidden sm:block w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link key={link.name} to={link.href}>
                  <motion.div
                    className="relative px-4 py-2 text-foreground/80 hover:text-primary font-medium transition-colors"
                    whileHover={{ y: -2 }}
                  >
                    {link.name}
                    <motion.div
                      className="absolute bottom-0 left-1/2 w-0 h-0.5 rounded-pill"
                      style={{ background: 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))' }}
                      whileHover={{ width: '80%', left: '10%' }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>
                </Link>
              ))}
              
              <PremiumMegaMenu />
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-2 xl:gap-3">
              <Link to="/listings">
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary rounded-full">
                    <Search className="w-5 h-5" />
                  </Button>
                </motion.div>
              </Link>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    className="rounded-pill font-semibold text-white px-4 xl:px-6 shadow-glow"
                  style={{ background: 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))' }}
                  onClick={() => setShowPatientAuth(true)}
                >
                  <User className="w-4 h-4 mr-2" />
                  Login
                </Button>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="xl:hidden p-2 text-foreground rounded-full hover:bg-muted shrink-0"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22 }}
                className="xl:hidden fixed inset-x-0 top-[96px] sm:top-[120px] z-[70] h-[calc(100dvh-96px)] sm:h-[calc(100dvh-120px)] overflow-hidden border-t border-border/50 bg-card shadow-[0_24px_60px_-28px_hsl(var(--primary)/0.35)]"
                style={{ backgroundColor: 'hsl(var(--card))' }}
              >
                <nav className="h-full w-full max-w-5xl mx-auto py-4 flex flex-col gap-1.5 overflow-x-hidden overflow-y-auto overscroll-contain custom-scroll bg-card pb-28">
                  <div className="px-2 grid grid-cols-2 gap-1.5">
                    {navLinks.map((link, idx) => (
                      <motion.div key={link.name} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
                        <Link
                          to={link.href}
                          onClick={() => setIsMenuOpen(false)}
                          className="block text-center text-sm font-semibold py-3 px-3 rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 to-healing-green/5 text-foreground hover:text-white hover:border-transparent transition-all hover:shadow-glow"
                          style={{
                            backgroundImage: undefined,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, hsl(213 69% 39%), hsl(152 62% 44%))'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                        >
                          {link.name}
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  <div className="px-4 pt-4">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-widest mb-2.5 font-bold flex items-center gap-2">
                      <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border" />
                      Special Services
                      <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
                    </p>
                    <div className="space-y-2">
                      {specialServices.map((service, idx) => (
                        <motion.div key={service.name} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: (navLinks.length + idx) * 0.04 }}>
                          <Link
                            to={service.href}
                            className="group flex items-center gap-3 py-2.5 px-3 rounded-2xl border border-transparent hover:border-healing-green/30 transition-all bg-gradient-to-r from-transparent to-transparent hover:from-primary/5 hover:to-healing-green/10"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div
                              className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-md group-hover:scale-110 transition-transform"
                              style={{ background: 'linear-gradient(135deg, hsl(213 69% 39%), hsl(152 62% 44%))' }}
                            >
                              <service.icon className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-foreground text-sm">{service.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{service.description}</p>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div className="px-4 pt-3">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-widest mb-2.5 font-bold flex items-center gap-2">
                      <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border" />
                      More Categories
                      <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border" />
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {moreServices.map((service, idx) => (
                        <motion.div key={service.name} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (navLinks.length + specialServices.length + idx) * 0.04 }}>
                          <Link
                            to={service.href}
                              className="flex h-full flex-col items-center gap-1.5 py-3 px-2 rounded-2xl border border-border/60 bg-gradient-to-br from-card to-healing-green/5 hover:border-healing-green/40 hover:shadow-md transition-all"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-sm"
                              style={{ background: 'linear-gradient(135deg, hsl(213 69% 39%), hsl(152 62% 44%))' }}
                            >
                              <service.icon className="w-4 h-4" />
                            </div>
                            <span className="text-[11px] font-semibold text-center text-foreground leading-tight break-words">{service.name}</span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div className="px-4 pt-4 pb-8 bg-card">
                    <Button
                      className="w-full h-12 rounded-pill font-semibold text-white shadow-glow"
                      style={{ background: 'linear-gradient(90deg, hsl(213 69% 39%), hsl(152 62% 44%))' }}
                      onClick={() => { setIsMenuOpen(false); setShowPatientAuth(true); }}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Login / Sign Up
                    </Button>
                  </div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
    
    <PatientAuthModal 
      isOpen={showPatientAuth} 
      onClose={() => setShowPatientAuth(false)}
      onSuccess={() => navigate('/patient/files')}
    />
    </>
  );
};

export default Header;
